import React from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle, Archive, ArrowRightCircle, ListTodo } from 'lucide-react';

import { Modal, Button, SelectField, Spinner, Badge } from '@dopomogai/ui';
import type { Sprint, ProjectTask } from '@dopomogai/types';

// --- Types & Constants ---

export interface CompletionDecision {
  taskId: string;
  action: 'move' | 'archive';
  // Optional target for 'move' actions
  targetSprintId?: string | null;
}

const ACTION_OPTIONS = [
    { value: 'archive', label: 'Return to Project Backlog' },
    { value: 'move', label: 'Move to another Sprint' },
];

const taskTypeColors: { [key: string]: 'blue' | 'purple' | 'green' | 'yellow' | 'red' | 'gray' } = {
    feature: 'blue',
    bugfix: 'red',
    chore: 'purple',
    docs: 'green',
    research: 'yellow',
    operations: 'gray',
    sales: 'gray',
    marketing: 'gray',
  };

// --- Main Component ---

interface SprintCompletionModalProps {
    sprint: Sprint | null;
    unfinishedTasks: ProjectTask[];
    // Pass in other sprints from the same project to populate the 'move' dropdown
    otherSprintsInProject: Sprint[]; 
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (decisions: CompletionDecision[]) => Promise<void>;
  }

/**
 * A modal to handle the workflow of completing a sprint, allowing the admin
 * to decide the fate of any unfinished tasks.
 */
export const SprintCompletionModal: React.FC<SprintCompletionModalProps> = ({
    sprint,
    unfinishedTasks,
    otherSprintsInProject,
    isOpen,
    onClose,
    onConfirm,
  }) => {
  const { t } = useTranslation(['admin_sprints', 'common']);
  const [isConfirming, setIsConfirming] = React.useState(false);
  const [decisions, setDecisions] = React.useState<CompletionDecision[]>([]);

  // Memoize the dropdown options for moving tasks
  const availableSprintOptions = React.useMemo(() => 
    otherSprintsInProject
        .filter(s => s.status === 'Planning' || s.status === 'Active')
        .map(s => ({ value: s.id, label: s.name })),
    [otherSprintsInProject]
  );
  
  React.useEffect(() => {
    // When the modal opens or the tasks change, reset the decisions
    if (isOpen) {
      setDecisions(unfinishedTasks.map(task => ({
        taskId: task._id,
        action: 'archive', // Default to the safest option
        targetSprintId: null,
      })));
    }
  }, [isOpen, unfinishedTasks]);

  const handleDecisionChange = (taskId: string, newValues: Partial<CompletionDecision>) => {
    setDecisions(current =>
      current.map(d => (d.taskId === taskId ? { ...d, ...newValues } : d))
    );
  };

  const handleConfirm = async () => {
    setIsConfirming(true);
    await onConfirm(decisions);
    setIsConfirming(false);
    // The parent component is responsible for closing the modal on success
  };
  
  const renderAllTasksCompleted = () => (
    <div className="p-6 text-center">
        <CheckCircle className="mx-auto h-12 w-12 text-emerald-500" />
        <p className="mt-4 text-lg font-semibold text-emerald-800">{t('completionModal.allTasksDoneTitle')}</p>
        <p className="text-slate-600 mt-1">
            {t('completionModal.allTasksDoneDescription')}
        </p>
    </div>
  );
  
  const renderUnfinishedTasks = () => (
    <div>
        <div className="p-4 mb-4 bg-amber-50 border-l-4 border-amber-400">
            <p className="text-amber-800">
                {t('completionModal.unfinishedWarning', { count: unfinishedTasks.length })}
            </p>
        </div>
        <ul className="space-y-3 max-h-[40vh] overflow-y-auto pr-2">
            {unfinishedTasks.map(task => {
                const decision = decisions.find(d => d.taskId === task._id);
                if (!decision) return null;

                return (
                  <li key={task._id} className="p-3 bg-slate-50 rounded-lg border">
                    {/* Task Info Row */}
                    <div className="flex justify-between items-center">
                        <span className="font-semibold text-slate-800 truncate pr-4">{task.title}</span>
                        <div className="flex items-center gap-2 flex-shrink-0">
                            <span className="text-xs font-bold text-slate-600">({task.points || 0} pts)</span>
                            <Badge text={task.task_type} accentColor={taskTypeColors[task.task_type]} />
                        </div>
                    </div>
                    {/* Action Row */}
                    <div className="mt-3 flex items-center gap-4 border-t pt-3">
                        <div className="w-56 flex-shrink-0">
                             <SelectField
                                name={`action-${task._id}`}
                                label={t('completionModal.actionLabel')}
                                value={decision.action}
                                onChange={(val) => handleDecisionChange(task._id, { action: val as 'move' | 'archive' })}
                                options={ACTION_OPTIONS}
                            />
                        </div>
                        {decision.action === 'move' && (
                             <div className="flex-grow">
                                <SelectField
                                    name={`target-${task._id}`}
                                    label={t('completionModal.targetSprintLabel')}
                                    value={decision.targetSprintId || ''}
                                    onChange={(val) => handleDecisionChange(task._id, { targetSprintId: val as string })}
                                    options={availableSprintOptions}
                                    placeholder={t('completionModal.selectSprintPlaceholder')}
                                    disabled={availableSprintOptions.length === 0}
                                />
                             </div>
                        )}
                    </div>
                  </li>
                );
            })}
        </ul>
    </div>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('completionModal.title', { sprintName: sprint?.name })} size="2xl">
        <div className="p-1">
            {unfinishedTasks.length === 0 ? renderAllTasksCompleted() : renderUnfinishedTasks()}
        </div>
        <div className="flex justify-end pt-5 mt-5 border-t gap-3">
            <Button
                textKey="common:cancel"
                variant="secondary"
                onClick={onClose}
                disabled={isConfirming}
            />
            <Button
                textKey="completionModal.confirmButton"
                onClick={handleConfirm}
                disabled={isConfirming || (unfinishedTasks.length > 0 && decisions.some(d => d.action === 'move' && !d.targetSprintId))}
                loading={isConfirming}
                icon={unfinishedTasks.length === 0 ? <CheckCircle size={16}/> : <Archive size={16}/>}
            />
        </div>
    </Modal>
  );
};